import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { a, useTransition } from 'react-spring';
import useMedia from 'utils/useMedia';

import { breakpoints } from 'Components/ThemeProvider';
import Typography from 'Components/Typography';
import { Link } from 'react-router-dom';
import Button from 'Components/LegacyButton';
import { useInView } from 'react-intersection-observer';

const styles = theme => ({
  fullWidth: props => ({
    backgroundColor: '#F1E6D7',
    backgroundImage: `url(${props.content.backgroundDesktop})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom left',
    backgroundSize: 'contain',
    width: '100%',
    height: '100vh',
    color: theme.palette.common.noir.medium,
    display: 'grid',
    gridTemplateColumns: '60% 40%',
    paddingTop: 100,
    '& div': {
      gridColumnStart: 2,
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
      maxWidth: '80%',
    },
    '& img': { padding: '40px 25px 0' },
    '& h2': {
      fontFamily: '"Simplon Mono"',
      textTransform: 'uppercase',
      marginBottom: 38,
      [theme.breakpoints.match('md')]: {
        textAlign: 'center',
      },
      [theme.breakpoints.ipadProPortrait]: {
        textAlign: 'center',
      },
      [theme.breakpoints.match('sm')]: {
        textAlign: 'center',
      },
    },
    '& p': {
      [theme.breakpoints.match('sm')]: {
        textAlign: 'center',
      },
    },
    '& a': {
      margin: '42px 0',
      display: 'block',
      [theme.breakpoints.match('md')]: {
        margin: '42px auto',
      },
      [theme.breakpoints.ipadProPortrait]: {
        margin: '42px auto',
      },
      [theme.breakpoints.match('sm')]: {
        margin: '42px auto',
      },
    },
    [theme.breakpoints.match('md')]: {
      backgroundImage: `url(${props.content.backgroundTablette})`,
      minHeight: '100vw',
      display: 'block',
    },
    [theme.breakpoints.match('sm')]: {
      backgroundImage: `url(${props.content.backgroundMobile})`,
      minHeight: '300vw', // works on all iphone till iphone 7
      backgroundPosition: 'bottom',
    },
    [theme.breakpoints.ipadProPortrait]: {
      backgroundImage: `url(${props.content.backgroundTablette})`,
      minHeight: '100vw',
      display: 'block',
    },
  }),
  heroWelcome: {
    height: '100%',
    [theme.breakpoints.match('md')]: {
      minHeight: '100vw',
      margin: '0 auto',
    },
    [theme.breakpoints.ipadProPortrait]: {
      height: '60vw',
      margin: '0 auto',
    },
    [theme.breakpoints.match('sm')]: {
      minHeight: '250vw', // works on all iphone till iphone 7
    },
    '& h1': {
      fontSize: '2.85vw',
      [theme.breakpoints.match('md')]: {
        maxWidth: 680,
        margin: '0 auto',
        textAlign: 'center',
        fontSize: 48,
      },
      [theme.breakpoints.ipadProPortrait]: {
        maxWidth: 680,
        margin: '0 auto',
        textAlign: 'center',
        fontSize: 48,
      },
      [theme.breakpoints.match('sm')]: {
        padding: '0 24px',
        margin: '0 auto',
        marginTop: 30,
        fontSize: 36,
      },
    },
    '& p': {
      maxWidth: 680,
      margin: 0,
      [theme.breakpoints.match('md')]: {
        maxWidth: 680,
        margin: '0 auto',
        textAlign: 'center',
      },
      [theme.breakpoints.ipadProPortrait]: {
        maxWidth: 680,
        margin: '0 auto',
        textAlign: 'center',
      },
      [theme.breakpoints.match('sm')]: {
        padding: '0 24px',
      },
    },
    '& a': {
      height: 40,
      [theme.breakpoints.match('md')]: {
        height: 'inherit',
      },
      [theme.breakpoints.ipadProPortrait]: {
        height: 'inherit',
        margin: '0 auto',
      },
      [theme.breakpoints.match('sm')]: {
        height: 'inherit',
      },
    },
  },
  transitionsItem: {
    display: 'block',
    overflow: 'hidden',
    height: 100,
  },
});

const WelcomeCustomCare = ({ classes, content }) => {
  // side-effect to fade-in the text
  const [descriptionRef, inView] = useInView({
    threshold: 0.2,
  });

  const { title, primaryTitle, changing, description, cta } = content;
  const [sentence, setSentence] = useState('');
  const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);
  const heightValue = isMobile ? 90 : 100;

  // animation styles
  const transitions = useTransition(sentence, null, {
    from: {
      opacity: 0,
      height: 0,
      innerHeight: 0,
    },
    enter: {
      opacity: 1,
      height: heightValue,
      innerHeight: heightValue,
    },
    leave: { opacity: 0, height: 0, innerHeight: 0 },
  });

  useEffect(() => {
    if (inView) {
      setSentence(changing[0]);
    }
  }, [inView]);

  useEffect(() => {
    if (inView) {
      const id = setTimeout(() => setSentence(changing[1]), 3000);
      return () => clearTimeout(id);
    }
    return () => {};
  }, [inView]);

  useEffect(() => {
    if (inView) {
      const id = setTimeout(() => setSentence(changing[2]), 6000);
      return () => clearTimeout(id);
    }
    return () => {};
  }, [inView]);

  return (
    <section className={classes.fullWidth}>
      <div className={classes.heroWelcome}>
        <Typography align="left" markupName="h2" variant="mono2">
          {title}
        </Typography>
        <Typography align="left" variant="hero2">
          {primaryTitle}{' '}
          {transitions.map(({ item, props: { innerHeight, ...rest }, key }) => (
            <a.span key={key} className={classes.transitionsItem} style={rest}>
              <a.span style={{ overflow: 'hidden', height: innerHeight }}>{item}</a.span>
            </a.span>
          ))}
        </Typography>
        <Typography ref={descriptionRef} align="left" variant="p2">
          {description}
        </Typography>
        <Button
          Component={Link}
          data-click="about"
          data-from="block-philosophy"
          to="/about"
          variant="underlinedBlack"
        >
          {cta}
        </Button>
      </div>
    </section>
  );
};

WelcomeCustomCare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    primaryTitle: PropTypes.string.isRequired,
    changing: PropTypes.arrayOf(PropTypes.string).isRequired,
    description: PropTypes.node.isRequired,
    cta: PropTypes.string.isRequired,
  }).isRequired,
};

export default withStyles(styles)(WelcomeCustomCare);
